import { useCallback, useEffect, useMemo, useRef } from 'react';

import UAParser from 'ua-parser-js';

import {
  AnalyticsEventAttributes,
  useAnalytics,
} from '@backstage/core-plugin-api';

import { confidenceApi } from '../../../apis/Apis';
import type { SignupAuthOutcome } from '../signupUtils';
import {
  type SignupAttribution,
  getAttributionCookie,
  getUtmParams,
} from './attribution';

const AUTH_REDIRECT_FLUSH_TIMEOUT_MS = 500;

const flushEventsWithTimeout = async (): Promise<void> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      Promise.resolve()
        .then(() => confidenceApi.flush())
        .catch(() => false),
      new Promise<void>(resolve => {
        timeoutId = setTimeout(resolve, AUTH_REDIRECT_FLUSH_TIMEOUT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
};

const getDeviceInfo = () => {
  const parser = new UAParser();
  const result = parser.getResult();

  let deviceType = 'desktop';
  if (result.device.type === 'mobile') deviceType = 'mobile';
  else if (result.device.type === 'tablet') deviceType = 'tablet';

  return {
    user_agent: navigator.userAgent,
    browser_name: result.browser.name || 'Unknown',
    browser_version: result.browser.version || 'Unknown',
    os_name: result.os.name || 'Unknown',
    device_type: deviceType,
  };
};

const sanitizeUrl = (url: string): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return null;
  }
};

const getReferrerInfo = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const utm = getUtmParams(searchParams);
  const referrerUrl = sanitizeUrl(document.referrer);
  const landingPageUrl = sanitizeUrl(window.location.href);

  const info: {
    referrer_url?: string;
    landing_page_url?: string;
    utm?: Record<string, string>;
    first_touch?: SignupAttribution;
  } = {};

  if (referrerUrl) info.referrer_url = referrerUrl;
  if (landingPageUrl) info.landing_page_url = landingPageUrl;
  if (Object.keys(utm).length > 0) info.utm = utm;

  const firstTouch = getAttributionCookie();
  if (firstTouch) info.first_touch = firstTouch;

  return info;
};

const generateId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2, 15)}`;

const SIGNUP_RUN_ID_STORAGE_KEY = 'signup-run-id-slim_wizard';
const SIGNUP_START_TIME_STORAGE_KEY = 'signup-start-time-slim_wizard';
const SIGNUP_AUTH_ATTEMPT_STORAGE_KEY = 'signup-auth-attempt-slim_wizard';

type SignupAuthAttempt = {
  attemptId: string;
  initiatedAt: number;
  returnedFromRedirect?: boolean;
};

const isSignupAuthAttempt = (value: unknown): value is SignupAuthAttempt =>
  typeof value === 'object' &&
  value !== null &&
  'attemptId' in value &&
  typeof value.attemptId === 'string' &&
  'initiatedAt' in value &&
  typeof value.initiatedAt === 'number';

const getStoredAuthAttempt = (): SignupAuthAttempt | undefined => {
  try {
    const storedAttempt = sessionStorage.getItem(
      SIGNUP_AUTH_ATTEMPT_STORAGE_KEY,
    );
    if (!storedAttempt) return undefined;
    const parsedAttempt: unknown = JSON.parse(storedAttempt);
    return isSignupAuthAttempt(parsedAttempt) ? parsedAttempt : undefined;
  } catch {
    return undefined;
  }
};

const persistAuthAttempt = (authAttempt: SignupAuthAttempt): void => {
  try {
    sessionStorage.setItem(
      SIGNUP_AUTH_ATTEMPT_STORAGE_KEY,
      JSON.stringify(authAttempt),
    );
  } catch {
    return;
  }
};

export const markAuthRedirectReturn = (): void => {
  const storedAuthAttempt = getStoredAuthAttempt();
  if (!storedAuthAttempt || storedAuthAttempt.returnedFromRedirect) return;
  persistAuthAttempt({ ...storedAuthAttempt, returnedFromRedirect: true });
};

const clearPersistedAuthAttempt = (): void => {
  try {
    sessionStorage.removeItem(SIGNUP_AUTH_ATTEMPT_STORAGE_KEY);
  } catch {
    return;
  }
};

export type SignupStepEvent = {
  step_id: string;
  step_name: string;
  step_index: number;
  step_count: number;
  step_version?: string;
  navigation?: {
    entry_mode: 'next' | 'back' | 'direct';
    previous_step_id?: string;
  };
};

export type SignupEventTrigger =
  | 'impression'
  | 'completion'
  | 'error'
  | 'interaction'
  | 'automatic';

type SignupLifecycleEvent =
  | {
      eventType: 'auth_initiated';
      trigger: 'interaction' | 'automatic';
      attemptId: string;
    }
  | {
      eventType: 'auth_completed';
      trigger: 'completion';
      attemptId: string;
      outcome: {
        status: SignupAuthOutcome;
        latency_ms: number;
        error_code?: string;
      };
    }
  | { eventType: 'verification_required'; trigger: 'impression' }
  | {
      eventType: 'verification_pending';
      trigger: 'completion';
      outcome: { status: 'pending' };
    }
  | {
      eventType: 'verification_succeeded';
      trigger: 'completion';
      outcome: { status: 'success' };
    }
  | {
      eventType: 'verification_check_failed';
      trigger: 'error';
      outcome: { status: 'error' };
    }
  | {
      eventType: 'reauthentication_required';
      trigger: 'error';
      outcome: { status: 'required' };
    };

export type SignupControlType =
  | 'text_input'
  | 'radio'
  | 'toggle_checkbox'
  | 'button';

export type SignupInteractionEvent = {
  control_id: string;
  control_type: SignupControlType;
  value?: string;
  checked?: boolean;
  form_state?: {
    storage_region: string;
    marketing_emails: boolean;
    auth_connections: string[];
  };
};

type OrgContext = {
  display_name?: string;
  workspace_name?: string;
};

type SignupTrackingOptions = {
  enabled?: boolean;
  hasAuthCallback?: boolean;
};

export const useSignupTracking = (options?: SignupTrackingOptions) => {
  const enabled = options?.enabled ?? true;
  const analytics = useAnalytics();
  const runIdRef = useRef<string>();
  const startTimeRef = useRef<number>();
  const previousStepRef = useRef<string>();
  const hasTrackedStartRef = useRef(false);
  const pendingAuthAttemptRef = useRef<SignupAuthAttempt | null | undefined>(
    undefined,
  );
  const restoredAuthAttemptIdRef = useRef<string | null>();
  if (!runIdRef.current) {
    try {
      const storedRunId = sessionStorage.getItem(SIGNUP_RUN_ID_STORAGE_KEY);
      if (storedRunId) {
        runIdRef.current = storedRunId;
        const storedStartTime = sessionStorage.getItem(
          SIGNUP_START_TIME_STORAGE_KEY,
        );
        const parsedStartTime = storedStartTime
          ? parseInt(storedStartTime, 10)
          : Date.now();
        startTimeRef.current = Number.isFinite(parsedStartTime)
          ? parsedStartTime
          : Date.now();
      } else {
        runIdRef.current = generateId();
        startTimeRef.current = Date.now();
        sessionStorage.setItem(SIGNUP_RUN_ID_STORAGE_KEY, runIdRef.current);
        sessionStorage.setItem(
          SIGNUP_START_TIME_STORAGE_KEY,
          startTimeRef.current.toString(),
        );
      }
    } catch {
      runIdRef.current = generateId();
      startTimeRef.current = Date.now();
    }
  }
  if (pendingAuthAttemptRef.current === undefined) {
    const restoredAuthAttempt = getStoredAuthAttempt();
    pendingAuthAttemptRef.current = restoredAuthAttempt ?? null;
    restoredAuthAttemptIdRef.current = restoredAuthAttempt?.attemptId ?? null;
  }
  const restoredAuthAttempt = pendingAuthAttemptRef.current;
  if (
    options?.hasAuthCallback === true &&
    restoredAuthAttempt &&
    !restoredAuthAttempt.returnedFromRedirect &&
    restoredAuthAttempt.attemptId === restoredAuthAttemptIdRef.current
  ) {
    pendingAuthAttemptRef.current = {
      ...restoredAuthAttempt,
      returnedFromRedirect: true,
    };
    markAuthRedirectReturn();
  }
  const runId = runIdRef.current;
  const pendingAuthAttempt = pendingAuthAttemptRef.current ?? undefined;
  const hasReturnedAuthAttempt =
    pendingAuthAttempt?.returnedFromRedirect === true &&
    pendingAuthAttempt.attemptId === restoredAuthAttemptIdRef.current;

  const visitorId = useMemo(() => confidenceApi.getContext().visitor_id, []);

  const staticContext = useMemo(
    () => ({
      run_id: runId,
      visitor_id: visitorId,
      flow: {
        flow_name: 'signup',
        variant: 'slim_wizard',
        flow_version: 'v3',
      },
      referrer: getReferrerInfo(),
      device: getDeviceInfo(),
    }),
    [runId, visitorId],
  );

  const orgContextRef = useRef<OrgContext>({});

  const setOrgContext = useCallback((org: OrgContext) => {
    orgContextRef.current = org;
  }, []);

  const trackStep = useCallback(
    (
      trigger: SignupEventTrigger,
      step: SignupStepEvent,
      additionalData?: {
        outcome?: {
          status: 'success' | 'error';
          latency_ms?: number;
          error_message?: string;
        };
      },
    ) => {
      const eventData = {
        event_name: 'signup_flow_event',
        event_type: 'step',
        trigger,
        event_time: new Date().toISOString(),
        ...staticContext,
        organization: orgContextRef.current,
        step,
        ...(additionalData || {}),
      };

      analytics.captureEvent('signup', 'signup-step', {
        attributes: eventData as unknown as AnalyticsEventAttributes,
      });

      previousStepRef.current = step.step_id;
    },
    [analytics, staticContext],
  );

  const trackInteraction = useCallback(
    (interaction: SignupInteractionEvent) => {
      const eventData = {
        event_name: 'signup_flow_event',
        event_type: 'interaction',
        trigger: 'interaction',
        event_time: new Date().toISOString(),
        ...staticContext,
        organization: orgContextRef.current,
        interaction,
      };

      analytics.captureEvent('signup', 'signup-interaction', {
        attributes: eventData as unknown as AnalyticsEventAttributes,
      });
    },
    [analytics, staticContext],
  );

  const trackLifecycle = useCallback(
    (event: SignupLifecycleEvent) => {
      const { eventType, trigger } = event;
      const outcome = 'outcome' in event ? event.outcome : undefined;
      const attemptId = 'attemptId' in event ? event.attemptId : undefined;
      const eventData = {
        event_name: 'signup_flow_event',
        event_type: eventType,
        trigger,
        event_time: new Date().toISOString(),
        ...staticContext,
        organization: orgContextRef.current,
        ...(outcome ? { outcome } : {}),
        ...(attemptId ? { attempt_id: attemptId } : {}),
      };

      if (['auth_initiated', 'auth_completed'].includes(eventType)) {
        try {
          confidenceApi.track('signup', {
            subject: eventType.replaceAll('_', '-'),
            value: 0,
            attributes: eventData,
          });
        } catch {
          return;
        }
        return;
      }

      analytics.captureEvent('signup', eventType.replaceAll('_', '-'), {
        attributes: eventData as unknown as AnalyticsEventAttributes,
      });
    },
    [analytics, staticContext],
  );

  const beginAuthRedirect = useCallback(
    ({ trigger }: { trigger: 'interaction' | 'automatic' }): Promise<void> => {
      const authAttempt: SignupAuthAttempt = {
        attemptId: generateId(),
        initiatedAt: Date.now(),
      };
      pendingAuthAttemptRef.current = authAttempt;
      persistAuthAttempt(authAttempt);
      trackLifecycle({
        eventType: 'auth_initiated',
        trigger,
        attemptId: authAttempt.attemptId,
      });
      return flushEventsWithTimeout();
    },
    [trackLifecycle],
  );

  const completeAuthRedirect = useCallback(
    (status: SignupAuthOutcome, errorCode?: string): void => {
      const authAttempt = pendingAuthAttemptRef.current;
      if (!authAttempt) return;
      pendingAuthAttemptRef.current = null;
      clearPersistedAuthAttempt();
      trackLifecycle({
        eventType: 'auth_completed',
        trigger: 'completion',
        attemptId: authAttempt.attemptId,
        outcome: {
          status,
          latency_ms: Math.max(0, Date.now() - authAttempt.initiatedAt),
          ...(errorCode ? { error_code: errorCode } : {}),
        },
      });
    },
    [trackLifecycle],
  );

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (event.persisted && pendingAuthAttemptRef.current) {
        completeAuthRedirect('abandoned');
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [completeAuthRedirect]);

  const trackCompletion = useCallback(
    (
      status: 'success' | 'abandoned' | 'error',
      additionalData?: {
        error_message?: string;
        final_step_id?: string;
      },
    ) => {
      const latencyMs = startTimeRef.current
        ? Date.now() - startTimeRef.current
        : undefined;

      const eventData = {
        event_name: 'signup_flow_event',
        event_type: 'completion',
        trigger: 'completion',
        event_time: new Date().toISOString(),
        ...staticContext,
        organization: orgContextRef.current,
        outcome: {
          status,
          latency_ms: latencyMs,
          ...(additionalData || {}),
        },
      };

      analytics.captureEvent('signup', 'signup-completion', {
        attributes: eventData as unknown as AnalyticsEventAttributes,
      });

      if (status === 'success') {
        sessionStorage.removeItem(SIGNUP_RUN_ID_STORAGE_KEY);
        sessionStorage.removeItem(SIGNUP_START_TIME_STORAGE_KEY);
      }
    },
    [analytics, staticContext],
  );

  useEffect(() => {
    if (!enabled || hasTrackedStartRef.current) return;
    hasTrackedStartRef.current = true;

    const eventData = {
      event_name: 'signup_flow_event',
      event_type: 'flow_start',
      trigger: 'impression',
      event_time: new Date().toISOString(),
      ...staticContext,
      organization: orgContextRef.current,
    };

    analytics.captureEvent('signup', 'signup-start', {
      attributes: eventData as unknown as AnalyticsEventAttributes,
    });
  }, [enabled, analytics, staticContext]);

  const getPreviousStepId = useCallback(() => previousStepRef.current, []);

  return useMemo(
    () => ({
      runId,
      visitorId,
      trackStep,
      trackInteraction,
      trackLifecycle,
      beginAuthRedirect,
      completeAuthRedirect,
      hasReturnedAuthAttempt,
      trackCompletion,
      setOrgContext,
      getPreviousStepId,
    }),
    [
      runId,
      visitorId,
      trackStep,
      trackInteraction,
      trackLifecycle,
      beginAuthRedirect,
      completeAuthRedirect,
      hasReturnedAuthAttempt,
      trackCompletion,
      setOrgContext,
      getPreviousStepId,
    ],
  );
};
